import { Box, Card, Divider, FormControl, Grid, MenuItem, Select } from '@mui/material'
import { useEffect, useState } from 'react'
import { getUserDetailsById } from '../utils/api'
import { BsArrowLeftCircleFill } from 'react-icons/bs'
import LinearProgress from '@mui/material/LinearProgress';
import DonutChart from '../components/DonutChart'
import { useNavigate } from 'react-router-dom'
import LoadingSkeleton from '../components/LoadingSkeleton'
import ExpendedTableView from '../components/ExpendedTableView'
import StackedBarChart from '../components/StackedBarChart';
import DateRangePicker from '../components/DateRangePicker';
import { onGetFormatedDateRange, getMonthAbbreviation } from '../utils/common';
import PieChartOutlineIcon from '@mui/icons-material/PieChartOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const ACCENT_BLUE = '#22638f';
const ACCENT_ORANGE = '#d96c3c';
const TEXT_MUTED = '#93a0b5';
const TEXT_BODY = '#3b4a63';
const BORDER = '#eef2f8';

const cardShell = {
    borderRadius: '16px',
    boxShadow: '0 2px 10px rgba(12, 61, 110, 0.05)',
    border: `1px solid ${BORDER}`,
}

const selectSx = {
    borderRadius: '10px',
    backgroundColor: '#fff',
    height: 42,
    '& .MuiOutlinedInput-notchedOutline': { borderColor: BORDER },
}

const CardHeader = ({ icon, title, accent }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', px: 2.5, py: 2 }}>
        <Box sx={{
            width: 34, height: 34, borderRadius: '10px',
            backgroundColor: `${accent}1A`, color: accent,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
            {icon}
        </Box>
        <p style={{ fontSize: 15, fontWeight: 700, color: TEXT_BODY, margin: 0 }}>{title}</p>
    </Box>
)


const getGranularity = (startDate, endDate) => {
    if (!startDate || !endDate) return 'day'
    const days = Math.ceil((new Date(endDate) - new Date(startDate)) / (1000 * 60 * 60 * 24))
    if (days <= 14) return 'day'
    if (days <= 90) return 'week'
    if (days <= 730) return 'month'
    return 'year'
}

const getMonday = (date) => {
    const d = new Date(date)
    const dayOfWeek = (d.getDay() + 6) % 7
    d.setDate(d.getDate() - dayOfWeek)
    d.setHours(0, 0, 0, 0)
    return d
}

const getBucketKeyAndLabel = (date, granularity) => {
    const d = new Date(date)

    if (granularity === 'day') {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
        const label = `${String(d.getDate()).padStart(2, '0')} ${getMonthAbbreviation(String(d.getMonth() + 1).padStart(2, '0'))}`
        return { key, label }
    }

    if (granularity === 'week') {
        const weekStart = getMonday(d)
        const key = `${weekStart.getFullYear()}-${String(weekStart.getMonth() + 1).padStart(2, '0')}-${String(weekStart.getDate()).padStart(2, '0')}`
        const label = `${String(weekStart.getDate()).padStart(2, '0')} ${getMonthAbbreviation(String(weekStart.getMonth() + 1).padStart(2, '0'))}`
        return { key, label }
    }

    if (granularity === 'month') {
        const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
        const label = `${getMonthAbbreviation(String(d.getMonth() + 1).padStart(2, '0'))} ${d.getFullYear()}`
        return { key, label }
    }

    // year
    const key = `${d.getFullYear()}`
    const label = `${d.getFullYear()}`
    return { key, label }
}

const buildAllBucketsInRange = (startDate, endDate, granularity) => {
    const buckets = []
    const seen = new Set()

    let cursor = new Date(startDate)
    const end = new Date(endDate)

    while (cursor <= end) {
        const { key, label } = getBucketKeyAndLabel(cursor, granularity)
        if (!seen.has(key)) {
            seen.add(key)
            buckets.push({ key, label })
        }

        if (granularity === 'day') cursor.setDate(cursor.getDate() + 1)
        else if (granularity === 'week') cursor.setDate(cursor.getDate() + 7)
        else if (granularity === 'month') cursor.setMonth(cursor.getMonth() + 1)
        else cursor.setFullYear(cursor.getFullYear() + 1)
    }

    // Always make sure the end date's own bucket is included (loop can undershoot on month/year steps)
    const { key: endKey, label: endLabel } = getBucketKeyAndLabel(end, granularity)
    if (!seen.has(endKey)) {
        buckets.push({ key: endKey, label: endLabel })
    }

    return buckets
}

function UserDetails() {
    const [userScanData, setUserScanData] = useState([])
    const [loading, setLoading] = useState(true);
    const [userLastLoaction, setUserListLoaction] = useState('')
    const [seriesList, setSeriesList] = useState([])
    const [labelsList, setLabelsList] = useState([])
    const [userData, setUserData] = useState('')
    const navigate = useNavigate()
    const minHeight = `calc(${window.innerHeight}px - 52px)`;
    const [filteredData, setFilteredDate] = useState([])
    const [dateRange, setDateRange] = useState('')
    const [selectedDateOption, setSelectedDateOption] = useState('this_week');
    const [isCustomDateSelected, setIsCustomDateSelected] = useState(false);
    const [customDateRange, setCustomDateRange] = useState({ startDate: '', endDate: '' })

    const [gradeChartCategories, setGradeChartCategories] = useState([])
    const [gradeChartSeries, setGradeChartSeries] = useState([])

    useEffect(() => {
        const today = new Date();
        const currentDay = today.getDay();
        const start_date = new Date(today);
        start_date.setDate(today.getDate() - currentDay);
        setDateRange({ startDate: start_date, endDate: today });
    }, [])

    useEffect(() => {
        (async () => {
            try {
                setLoading(true)
                const path = window.location.pathname;
                const id = path.split("/").pop();
                if (dateRange?.startDate && dateRange?.endDate) {
                    const response = await getUserDetailsById(id, dateRange?.startDate, dateRange?.endDate);
                    if (response?.data?.data) {
                        setUserData(response?.data?.data?.user_data)

                        const dataList = response?.data?.data && response?.data?.data?.scans?.map((item, index) => (
                            { ...item, id: index }
                        ))

                        response?.data?.data && setUserScanData(dataList)
                        response?.data?.data && showOnlyUniqueScanIDData(dataList)
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false)
            }
        })()
    }, [dateRange])

    useEffect(() => {
        if (customDateRange && customDateRange.length === 2) {
            const startDate = new Date(customDateRange[0].format());
            const endDate = new Date(customDateRange[1].format());
            startDate.setDate(startDate.getDate());
            startDate.setHours(0, 0, 0, 0);
            endDate.setDate(endDate.getDate());
            endDate.setHours(23, 59, 59, 999);
            setDateRange({ startDate: startDate, endDate: endDate });
        }
    }, [customDateRange])

    const donutDataList = (dataList) => {
        if (dataList) {
            const map = new Map()
            for (let item of dataList) {
                if (map.has(item?.crop_details.crop_name)) {
                    map.set(item?.crop_details.crop_name, map.get(item?.crop_details.crop_name) + 1)
                } else {
                    map.set(item?.crop_details.crop_name, 1)
                }
            }
            const label = []
            const series = []
            for (let item of map.keys()) {
                label.push(item)
                series.push(map.get(item))
            }
            setLabelsList(label)
            setSeriesList(series)
        }
    }

    const buildGradeDateChart = (dataList) => {
        const gradeRanges = [
            { name: "Grade A", min: 90, max: 100 },
            { name: "Grade B", min: 80, max: 90 },
            { name: "Grade C", min: -1, max: 80 },
        ];

        const granularity = getGranularity(dateRange?.startDate, dateRange?.endDate)

        const allBuckets = buildAllBucketsInRange(dateRange.startDate, dateRange.endDate, granularity)

        // If every bucket falls within the same calendar year, drop the year from month labels
        if (granularity === 'month') {
            const years = new Set(allBuckets.map(b => b.key.split('-')[0]))
            if (years.size === 1) {
                allBuckets.forEach(b => {
                    b.label = b.label.split(' ')[0] // "Jan 2026" -> "Jan"
                })
            }
        }

        const bucketIndexByKey = {}
        allBuckets.forEach((b, i) => { bucketIndexByKey[b.key] = i })

        const gradeCounts = gradeRanges.map(() => new Array(allBuckets.length).fill(0))

        dataList.forEach((item) => {
            const score = item.overall_score ?? item.kernel_details?.overall_score
            if (!score) return

            const { key } = getBucketKeyAndLabel(item.scan_date, granularity)
            const bucketIndex = bucketIndexByKey[key]
            if (bucketIndex === undefined) return

            let gradeIndex = 2
            for (let i = 0; i < gradeRanges.length; i++) {
                if (score >= gradeRanges[i].min && score <= gradeRanges[i].max) { gradeIndex = i; break }
            }
            gradeCounts[gradeIndex][bucketIndex] += 1
        })

        const series = gradeRanges.map((grade, i) => ({
            name: grade.name,
            data: gradeCounts[i],
        }))

        setGradeChartCategories(allBuckets.map(b => b.label))
        setGradeChartSeries(series)
    }

    const showOnlyUniqueScanIDData = async (dataList) => {
        const uniqueScanData = {};
        const uniqueDataList = dataList.filter((item) => {
            if (!uniqueScanData[item.scan_id]) {
                uniqueScanData[item.scan_id] = true;
                return true;
            }
            return false;
        });
        buildGradeDateChart(uniqueDataList)
        const countData = uniqueDataList.map((item) => {
            const subScanCount = dataList.filter((subItem) => subItem.scan_id === item.scan_id).length;
            return { ...item, sub_scan_count: subScanCount };
        });
        setFilteredDate(countData);
        setUserListLoaction(dataList[0]?.scan_metadata?.location_name || "-");
        Promise.all([
            donutDataList(countData),
        ]);
    };

    const handleOptionDateChange = (event) => {
        const selectedOption = event.target.value;

        if (selectedOption === 'custom') {
            setIsCustomDateSelected(true);
        } else {
            setIsCustomDateSelected(false);
            const formated_date_range = onGetFormatedDateRange(selectedOption);
            setDateRange({ startDate: formated_date_range.start_date, endDate: formated_date_range.end_date });
        }
        setSelectedDateOption(selectedOption);
    };

    const hasGradeChartData = gradeChartSeries.some(s => s.data.some(v => v > 0))

    return (
        <div style={{ backgroundColor: '#eef4fb', minHeight: minHeight }}>
            {userData ? <Box>
                <Box sx={{ p: 3 }}>
                    <Card sx={{ ...cardShell, backgroundColor: '#fff' }}>
                        {loading && <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: ACCENT_ORANGE }, backgroundColor: BORDER }} />}
                        <Box sx={{ textAlign: 'left', p: '20px 22px' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 1.5 }}>
                                    <Box
                                        onClick={() => navigate('/users')}
                                        sx={{
                                            width: 34, height: 34, borderRadius: '10px',
                                            backgroundColor: 'rgba(34, 93, 153, 0.08)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            cursor: 'pointer', flexShrink: 0,
                                        }}
                                    >
                                        <BsArrowLeftCircleFill style={{ fontSize: 17, color: ACCENT_BLUE }} />
                                    </Box>
                                    <Box>
                                        <Box sx={{ fontSize: 22, fontWeight: 700, color: TEXT_BODY }}>
                                            {userData?.first_name} {userData?.last_name}
                                        </Box>
                                        <Box sx={{ fontSize: 13.5, fontWeight: 500, color: TEXT_MUTED, mt: 0.3 }}>
                                            {userData?.email ? userData?.email : userData?.phone_number}
                                        </Box>
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 1.5 }}>
                                    <FormControl sx={{ minWidth: 170 }}>
                                        <Select
                                            value={selectedDateOption}
                                            sx={selectSx}
                                            onChange={handleOptionDateChange}
                                            displayEmpty
                                        >
                                            <MenuItem value="all">All</MenuItem>
                                            <MenuItem value="today">Today</MenuItem>
                                            <MenuItem value='this_week'>This Week</MenuItem>
                                            <MenuItem value='this_month'>This Month</MenuItem>
                                            <MenuItem value='this_year'>This Year</MenuItem>
                                            <MenuItem value='custom'>Choose a date</MenuItem>
                                        </Select>
                                    </FormControl>
                                    {isCustomDateSelected && <DateRangePicker setDateRange={setCustomDateRange} />}
                                </Box>
                            </Box>

                            <Box sx={{ mt: 2.5, p: '16px 20px', border: `1px solid ${BORDER}`, borderRadius: '12px', backgroundColor: '#f8fbfe' }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <p style={{ fontWeight: 600, color: TEXT_MUTED, fontSize: 13.5, margin: 0 }}>Last Scan Location</p>
                                            <p style={{ fontWeight: 700, fontSize: 13.5, color: TEXT_BODY, margin: 0 }}>
                                                {userLastLoaction ? userLastLoaction !== 'undefined, undefined' ? userLastLoaction : '-' : '-'}
                                            </p>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', borderLeft: { sm: `1px solid ${BORDER}` }, pl: { sm: 2 } }}>
                                            <p style={{ fontWeight: 600, color: TEXT_MUTED, fontSize: 13.5, margin: 0 }}>Total Scans</p>
                                            <p style={{ fontWeight: 700, fontSize: 13.5, color: TEXT_BODY, margin: 0 }}>
                                                {filteredData?.length ? filteredData?.length : '-'}
                                            </p>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                    </Card>

                    {!loading ? <Box>
                        <Box sx={{ mt: 2.5, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '18px' }}>
                            {seriesList?.length ? (
                                <Card sx={{ ...cardShell, flex: '1 1 420px', backgroundColor: '#fff', overflow: 'hidden' }}>
                                    <CardHeader icon={<PieChartOutlineIcon sx={{ fontSize: 19 }} />} title="Scan Distribution" accent={ACCENT_ORANGE} />
                                    <Divider />
                                    <Box sx={{ p: 2 }}>
                                        <DonutChart series={seriesList} labels={labelsList} title="Total Scans" />
                                    </Box>
                                </Card>
                            ) : null}

                            {hasGradeChartData ? (
                                <Card sx={{ ...cardShell, flex: '1 1 420px', backgroundColor: '#fff', overflow: 'hidden' }}>
                                    <CardHeader icon={<TrendingUpIcon sx={{ fontSize: 19 }} />} title="Grade Vs Date" accent={ACCENT_BLUE} />
                                    <Divider />
                                    <Box sx={{ p: 2 }}>
                                        <StackedBarChart
                                            seriesData={gradeChartSeries}
                                            categoriesData={gradeChartCategories}
                                            title=""
                                            xaxis_title="Date"
                                            yaxis_title="Number Of Scans"
                                        />
                                    </Box>
                                </Card>
                            ) : null}
                        </Box>
                        {userScanData && userScanData.length > 0 &&
                            <Box sx={{ mt: 2.5 }}>
                                <ExpendedTableView dataList={userScanData} />
                            </Box>
                        }
                    </Box> : null}
                </Box>
            </Box> : <LoadingSkeleton />}
        </div>
    )
}

export default UserDetails